import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataState,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

type Role = "admin" | "operator" | "viewer";

interface MemberRow {
  id: string;
  email: string;
  name: string;
  isFpzeroAdmin: boolean;
  role: Role;
  joinedAt: string;
}

export function Members() {
  const { currentOrg } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MemberRow | null>(null);

  const members = useQuery<MemberRow[]>(
    currentOrg ? `/api/organisations/${currentOrg.id}/members` : null,
  );

  if (!currentOrg) return null;

  const isAdmin = canIDo("members:invite", currentOrg.id);

  async function changeRole(member: MemberRow, role: Role) {
    setBusyId(member.id);
    try {
      const res = await api(`/api/organisations/${currentOrg!.id}/members/${member.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to change role", "error");
        return;
      }
      addToast(`${member.name} is now ${role}`);
      members.refetch();
    } finally {
      setBusyId(null);
    }
  }

  async function remove(member: MemberRow) {
    setBusyId(member.id);
    try {
      const res = await api(`/api/organisations/${currentOrg!.id}/members/${member.id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to remove member", "error");
        return;
      }
      addToast(`${member.name} removed`);
      members.refetch();
    } finally {
      setBusyId(null);
      setRemoving(null);
    }
  }

  const columns: Column<MemberRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (m) => (
        <div>
          <div className="font-medium text-gray-900">{m.name}</div>
          <div className="text-xs text-gray-500">{m.email}</div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (m) =>
        isAdmin ? (
          <Select
            compact
            value={m.role}
            onChange={(e) => changeRole(m, e.target.value as Role)}
            disabled={busyId === m.id}
            options={[
              { value: "admin", label: "Admin" },
              { value: "operator", label: "Operator" },
              { value: "viewer", label: "Viewer" },
            ]}
          />
        ) : (
          <Badge className="bg-gray-100 text-gray-700">{m.role}</Badge>
        ),
    },
    {
      key: "fpzero",
      header: "",
      render: (m) =>
        m.isFpzeroAdmin ? (
          <Badge className="bg-purple-100 text-purple-700">FPZERO</Badge>
        ) : null,
    },
    {
      key: "joinedAt",
      header: "Joined",
      render: (m) => (
        <span className="text-sm text-gray-500">{new Date(m.joinedAt).toLocaleDateString()}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (m) =>
        isAdmin ? (
          <div className="text-right">
            <Button
              size="sm"
              variant="secondary"
              loading={busyId === m.id}
              onClick={() => setRemoving(m)}
            >
              Remove
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Members" subtitle={`Users with access to ${currentOrg.name}.`}>
        {isAdmin && <Button onClick={() => setInviting(true)}>Invite member</Button>}
      </PageHeader>

      <Card className="overflow-hidden">
        <DataState
          loading={members.loading}
          error={members.error}
          empty={!members.loading && (members.data ?? []).length === 0}
          emptyMessage="No members yet."
        >
          <Table<MemberRow>
            columns={columns}
            data={members.data ?? []}
            keyExtractor={(m) => m.id}
          />
        </DataState>
      </Card>

      <InviteModal
        open={inviting}
        onClose={() => setInviting(false)}
        onCreated={() => members.refetch()}
        orgId={currentOrg.id}
      />

      <ConfirmDialog
        isOpen={!!removing}
        title="Remove member?"
        message={
          removing
            ? `Remove ${removing.name} from ${currentOrg.name}? They'll lose access immediately. The user account is preserved.`
            : ""
        }
        confirmLabel="Remove"
        loading={!!removing && busyId === removing.id}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove(removing)}
      />
    </div>
  );
}

function InviteModal({
  open,
  onClose,
  onCreated,
  orgId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  orgId: string;
}) {
  const api = useApi();
  const { addToast } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [tempPassword, setTempPassword] = useState(generatePassword());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setEmail("");
    setName("");
    setRole("viewer");
    setTempPassword(generatePassword());
    setError(null);
  }

  async function submit() {
    if (!email || !name) {
      setError("Email and name are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api(`/api/organisations/${orgId}/members`, {
        method: "POST",
        body: JSON.stringify({ email, name, role, tempPassword }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error ?? "Failed to invite");
        return;
      }
      addToast(`${name} invited — share the temp password securely.`);
      onCreated();
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={open} onClose={() => { reset(); onClose(); }} title="Invite member">
      <div className="space-y-4">
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          options={[
            { value: "viewer", label: "Viewer (read-only)" },
            { value: "operator", label: "Operator (drivers, events, bookings)" },
            { value: "admin", label: "Admin (full org control)" },
          ]}
        />
        <div>
          <Input
            label="Temporary password"
            value={tempPassword}
            onChange={(e) => setTempPassword(e.target.value)}
          />
          <div className="mt-1 text-xs text-gray-500">
            Share this with the user via a secure channel. They'll be prompted
            to rotate it on first login (CU.11).
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => { reset(); onClose(); }}>
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting}>
            Send invite
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function generatePassword(): string {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const out: string[] = [];
  // 16 characters → exceeds 12-char minimum on the backend.
  const arr = new Uint32Array(16);
  crypto.getRandomValues(arr);
  for (const v of arr) out.push(charset[v % charset.length]);
  return out.join("");
}
